import { useParams, Link, Navigate } from 'react-router-dom';
import { BLOG_POSTS, BRAND } from '@/data/content';
import * as Icons from 'lucide-react';
import { Seo, Breadcrumbs, BUSINESS_ID, SITE_URL } from '@/components/site/Seo';
import FinalCTA from '@/components/site/FinalCTA';

// Render inline **bold** and [text](url) — internal links go through <Link>, external stay <a>.
function renderInline(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let key = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`b${key++}`} className="text-brand-charcoal">{tok.slice(2, -2)}</strong>);
    } else {
      const linkM = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const label = linkM[1];
      const href = linkM[2];
      const isExternal = /^https?:\/\//.test(href);
      const cls = 'text-brand-maroon underline decoration-brand-maroon/40 hover:decoration-brand-maroon underline-offset-4 transition-colors';
      if (isExternal) {
        parts.push(<a key={`l${key++}`} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{label}</a>);
      } else {
        parts.push(<Link key={`l${key++}`} to={href} className={cls}>{label}</Link>);
      }
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function Block({ block }) {
  const lines = block.split('\n');
  const first = lines[0];

  if (first.startsWith('## ')) {
    return <h2 className="font-serif text-2xl md:text-3xl text-brand-charcoal mt-12 mb-1 tracking-tight">{first.slice(3)}</h2>;
  }
  if (first.startsWith('### ')) {
    return <h3 className="font-serif text-xl text-brand-charcoal mt-10 mb-1">{first.slice(4)}</h3>;
  }

  // Table: first line starts with |, second line is separator like |---|---|
  if (first.startsWith('|') && lines[1] && /^\|[\s\-:|]+\|$/.test(lines[1].trim())) {
    const parsed = lines
      .filter((l) => l.trim().startsWith('|'))
      .filter((l) => !/^\|[\s\-:|]+\|$/.test(l.trim()))
      .map((l) => l.trim().split('|').slice(1, -1).map((c) => c.trim()));
    const [head, ...rows] = parsed;
    return (
      <div className="my-8 overflow-x-auto border border-brand-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-brand-boneAlt">
              {head.map((c, i) => (
                <th key={i} className="text-left font-sans font-semibold uppercase tracking-[0.14em] text-[11px] text-brand-charcoal px-4 py-3 border-b border-brand-border">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 1 ? 'bg-brand-boneAlt/40' : ''}>
                {row.map((c, i) => (
                  <td key={i} className={`px-4 py-3 font-sans text-brand-charcoal border-b border-brand-border/70 ${i === row.length - 1 ? 'font-serif text-brand-maroon whitespace-nowrap' : ''}`}>
                    {renderInline(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Bullet list: every line starts with "- " or "• "
  if (lines.every((l) => /^[-•]\s+/.test(l))) {
    return (
      <ul className="list-disc pl-6 space-y-2 marker:text-brand-maroon my-2">
        {lines.map((l, i) => (
          <li key={i} className="pl-1">{renderInline(l.replace(/^[-•]\s+/, ''))}</li>
        ))}
      </ul>
    );
  }

  // Ordered list: every line starts with "1. ", "2. ", ...
  if (lines.every((l) => /^\d+\.\s+/.test(l))) {
    return (
      <ol className="list-decimal pl-6 space-y-2 marker:text-brand-maroon marker:font-serif marker:font-semibold my-2">
        {lines.map((l, i) => (
          <li key={i} className="pl-1">{renderInline(l.replace(/^\d+\.\s+/, ''))}</li>
        ))}
      </ol>
    );
  }

  return <p>{renderInline(block)}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  if (!post) return <Navigate to="/blog" replace />;

  const path = `/blog/${post.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.iso,
    dateModified: post.iso,
    author: { '@type': 'Person', name: 'Ben Hardy', jobTitle: 'Owner, HearthOak Construction, LLC' },
    publisher: { '@id': BUSINESS_ID },
    mainEntityOfPage: `${SITE_URL}${path}`,
  };

  const blocks = post.body.split(/\n\n+/);

  return (
    <div data-testid={`page-blog-${slug}`}>
      <Seo
        title={`${post.title} | HearthOak Construction`}
        description={post.excerpt}
        path={path}
        type="article"
        jsonLd={jsonLd}
      />
      <article className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-14 lg:py-20 max-w-3xl">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }, { label: post.title }]}
            testId={`breadcrumbs-blog-${slug}`}
          />
          <div className="mt-2 flex items-center gap-3 font-sans text-xs uppercase tracking-[0.22em] text-brand-muted">
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.read} read</span>
            <span>·</span>
            <span>By Ben Hardy, {BRAND.short}</span>
          </div>
          <h1 className="h1 mt-4">{post.title}</h1>
          <p className="body-lg mt-6 font-serif text-xl text-brand-charcoal/80">{post.excerpt}</p>

          <div className="mt-10 space-y-5 font-sans text-base lg:text-[17px] leading-[1.85] text-brand-charcoal">
            {blocks.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>

          <div className="mt-14 border-t border-brand-border pt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div className="font-serif text-brand-charcoal/85">Have a project you want to talk through?</div>
            <Link to="/estimate" className="btn-primary" data-testid={`blog-cta-${slug}`}>Request an Estimate</Link>
          </div>
        </div>
      </article>

      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <div className="eyebrow mb-3">Keep reading</div>
          <h2 className="h2 max-w-xl">More helpful articles</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/blog/${o.slug}`} className="p-6 border border-brand-border bg-brand-bone hover:bg-brand-boneAlt transition-colors group">
                <div className="font-sans text-xs uppercase tracking-[0.22em] text-brand-muted">{o.date} · {o.read} read</div>
                <div className="mt-3 font-serif text-lg text-brand-charcoal">{o.title}</div>
                <div className="mt-5 font-sans text-xs uppercase tracking-[0.22em] text-brand-maroon inline-flex items-center gap-1.5">
                  Read <Icons.ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
